import { Link } from "@tanstack/react-router";
import { formatDistanceToNowStrict } from "date-fns";
import { useRecentRouteChanges } from "@/api/hooks/recent-routes";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";

interface RecentRoutesTableProps {
  className?: string;
  limit?: number;
}

export function RecentRoutesTable({ className, limit = 10 }: RecentRoutesTableProps) {
  const { data, isLoading, isError } = useRecentRouteChanges(limit);

  if (isLoading) {
    return (
      <Skeleton className={cn("h-40 w-full", className)} data-testid="recent-routes-skeleton" />
    );
  }

  if (isError) {
    return (
      <p role="alert" className={cn("text-sm text-destructive", className)}>
        Failed to load recent route changes
      </p>
    );
  }

  const rows = data ?? [];
  if (rows.length === 0) {
    return (
      <p className={cn("text-sm text-muted-foreground", className)}>No recent route changes</p>
    );
  }

  return (
    <Table className={className}>
      <TableHeader>
        <TableRow>
          <TableHead>Pair</TableHead>
          <TableHead>Protocol</TableHead>
          <TableHead>Observed</TableHead>
          <TableHead className="text-right">Action</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={row.id}>
            <TableCell className="font-mono text-xs">
              {row.source_id} → {row.target_id}
            </TableCell>
            <TableCell className="uppercase text-xs">{row.protocol}</TableCell>
            <TableCell className="text-xs text-muted-foreground">
              {formatDistanceToNowStrict(new Date(row.observed_at), {
                addSuffix: true,
              })}
            </TableCell>
            <TableCell className="text-right">
              <Link
                to={"/paths/$source/$target"}
                params={{ source: row.source_id, target: row.target_id }}
                search={{ range: "24h" }}
                className="text-sm underline underline-offset-2"
              >
                view
              </Link>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
